import { useEffect, useRef, useState } from 'react'
import type { ChartAppDependencies } from '@dx-private/dxchart5-react/dist/chart/chart-react-app'
import { loadLicensedWidgetRuntime, LicensedWidgetStartupError } from '../lib/chart/licensed-widget-loader'
import { licensedWidgetLog } from '../lib/debug/browser-logging'
import {
  resolveChartSizingProfile,
  withMobileCanvasTypography,
  type ChartSizingProfile,
} from '../lib/chart/mobile-chart-sizing'

interface LicensedChartWidgetHostProps {
  readonly dependencies: ChartAppDependencies
  readonly onApiCreated?: (api: any) => void
  readonly onApiDestroyed?: () => void
  readonly uiOverrides?: any
  readonly onInitializationError?: (error: Error) => void
  readonly className?: string
}

const HiddenLicensedWidgetLogo = () => null

export function LicensedChartWidgetHost({
  dependencies,
  onApiCreated,
  onApiDestroyed,
  uiOverrides,
  onInitializationError,
  className,
}: LicensedChartWidgetHostProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const onApiCreatedRef = useRef(onApiCreated)
  const onApiDestroyedRef = useRef(onApiDestroyed)
  const onInitializationErrorRef = useRef(onInitializationError)

  onApiCreatedRef.current = onApiCreated
  onApiDestroyedRef.current = onApiDestroyed
  onInitializationErrorRef.current = onInitializationError

  // Canvas label fonts are initial chart configuration rather than CSS, and the
  // vendor exposes no runtime config-update path, so a breakpoint crossing has
  // to recreate the widget. Storing the resolved profile (not the raw width)
  // means ordinary same-profile resizes never change this state and therefore
  // never re-run the initialization effect below.
  //
  // Recreation resets widget-owned state (zoom, pan, drawings, selection). This
  // is a known, accepted limitation for this iteration: drawings and selection
  // have no exposed save/restore, so the state cannot be carried across. See
  // openspec/changes/increase-mobile-chart-readability/design.md.
  const [sizingProfile, setSizingProfile] = useState<ChartSizingProfile>(() =>
    resolveChartSizingProfile(typeof window === 'undefined' ? 0 : window.innerWidth),
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const syncProfile = () => {
      setSizingProfile(resolveChartSizingProfile(window.innerWidth))
    }

    syncProfile()
    window.addEventListener('resize', syncProfile)
    return () => {
      window.removeEventListener('resize', syncProfile)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let destroyWidget: (() => void) | null = null
    let publishedApi: any = null
    const container = containerRef.current

    if (!container) {
      return
    }

    const initialize = async () => {
      try {
        const runtime = await loadLicensedWidgetRuntime()
        // Caller configuration is passed through otherwise verbatim; only the
        // invariant watermark/logo suppression and the responsive mobile canvas
        // font sizes are layered on top. The mobile merge keeps every other
        // caller-provided component setting (notably the tournament's Y-axis
        // labels configuration); an explicit caller axis fontSize is incremented
        // by one pixel rather than replaced.
        const sizedChartConfig = withMobileCanvasTypography(
          dependencies.initialChartConfig,
          sizingProfile,
        ) as ChartAppDependencies['initialChartConfig']
        const effectiveDependencies: ChartAppDependencies = {
          ...dependencies,
          initialChartConfig: {
            ...(sizedChartConfig ?? {}),
            components: {
              ...(sizedChartConfig?.components ?? {}),
              waterMark: {
                ...(sizedChartConfig?.components?.waterMark ?? {}),
                visible: false,
              },
            },
          },
        }
        const effectiveUiOverrides = {
          ...(uiOverrides ?? {}),
          Logo: HiddenLicensedWidgetLogo,
        }
        let createdApi: any = null
        const widgetProps = {
          dependencies: effectiveDependencies,
          xAxisEnabled: true,
          yAxisEnabled: true,
          onAPICreated: (api: any) => {
            createdApi = api
            // The vendor may invoke this after createWidget()'s promise has already
            // resolved, so publishing/tracking the API happens here — not in the
            // `if (createdApi)` check below, which can run before this ever fires.
            if (!cancelled && !publishedApi) {
              publishedApi = api
              onApiCreatedRef.current?.(api)
            }
          },
          uiOverrides: effectiveUiOverrides,
        } as unknown as import('@dx-private/dxchart5-react-widget/dist/widget.config').ChartReactWidgetProps
        const widgetInstance = await runtime.createWidget!(container, widgetProps)

        if (cancelled) {
          // StrictMode or rapid dependency churn can resolve createWidget after this effect was cleaned up.
          // If the original container is gone, skip destroy to avoid vendor DOM teardown races.
          if (!container.isConnected) {
            return
          }

          try {
            widgetInstance.destroy()
          } catch {
            // ignore teardown races while the effect is being replaced
          }
          return
        }

        if (createdApi && !publishedApi) {
          publishedApi = createdApi
          onApiCreatedRef.current?.(createdApi)
        }

        destroyWidget = () => {
          try {
            widgetInstance.destroy()
          } catch {
            // ignore teardown races caused by DOM churn in strict-mode remount flows
          }
        }
      } catch (error) {
        if (cancelled) {
          return
        }

        const normalizedError =
          error instanceof LicensedWidgetStartupError
            ? error
            : new LicensedWidgetStartupError(
                error instanceof Error
                  ? error.message
                  : 'Licensed widget initialization failed.',
                [],
              )
        licensedWidgetLog('startup failed', {
          message: normalizedError.message,
          diagnostics: normalizedError.diagnostics,
        })
        onInitializationErrorRef.current?.(normalizedError)
      }
    }

    void initialize()

    return () => {
      cancelled = true
      if (publishedApi) {
        onApiDestroyedRef.current?.()
        publishedApi = null
      }
      destroyWidget?.()
      destroyWidget = null
    }
  }, [dependencies, uiOverrides, sizingProfile])

  return (
    <div
      ref={containerRef}
      className={className}
      data-chart-host="licensed"
      style={{ width: '100%', height: '100%' }}
    />
  )
}
